import Sortable from "sortablejs";
import { applySortableReorder, createReorderable } from "../shared/sortable-reorder";
import { getCSRFToken, dispatchToast } from "../shared/htmx-helpers";
import { signalPublishStateChanged } from "../shared/publish-state-signal";

// One cell of a repeater row, projected from the Go CMSRepeaterRowField. `widget`
// drives which input the templ renders ('icon_select' vs text); `key` is the
// object key within each row (e.g. 'icon', 'text').
interface RepeaterCell {
  key: string;
  widget: string;
  label: string;
  multiline?: boolean;
  options?: { value: string; label: string }[];
  // Emit on change (blur/Enter) instead of debounced input — see the templ comment.
  commit_on_blur?: boolean;
  // Select-cell value unique across rows — dropdown disables taken options.
  unique?: boolean;
}

// A row as edited in the controller: the schema cell values (string) plus a
// stable client key (`_k`, number) used only for Alpine's keyed x-for. `_k` is
// stripped before emit so the persisted value is the clean {icon,text,…} object
// the content expects. The index signature is `string | number` because TS can't
// express "every key except `_k` is a string"; reads of cell values go through
// `String(...)`-safe paths (x-model + the emit projection) so this stays sound.
interface RepeaterRow {
  _k: number;
  // _persisted marks a row that exists SERVER-SIDE (seeded from the initial
  // content read) vs one created client-side this session via addRow() and
  // never round-tripped. removeRow() routes a persisted row's delete through
  // the operator-UI row-delete endpoint (the SAME transactional splice +
  // provenance re-index chokepoint the assistant's remove tool uses, Task
  // 8B); an unsaved row has no server-side row to reindex, so it stays a
  // pure client splice + the existing array-blind emit().
  _persisted: boolean;
  [cell: string]: string | number | boolean;
}

interface CMSRepeaterConfig {
  field: string;
  // Autosave routing key (which content file + overlay shape). Threaded through
  // the cms-image-changed event detail so the controller routes the write. See
  // service.CMSTarget* / editor.ts `_flush`.
  target: string;
  schema: RepeaterCell[];
  rows: Record<string, string>[];
  // aiCells seeds the inline per-cell "edited by assistant" badge + revert
  // (design §8 inline parity): "<rowIndex>.<cell>" → pre-assistant value.
  // editor.ts's _applyListCellEdit adds entries live when an assistant edit
  // lands on-screen this session.
  aiCells?: Record<string, string>;
  // aiLeafBase is the repeater array's AI-edit-store leaf-path base (e.g.
  // "sections.faq.faq_items"); the controller appends ".<rowIndex>.<cell>" to
  // address the exact row it clears on revert/override (the ack leaf).
  aiLeafBase?: string;
  // publishedCells seeds the manual-edit revert baseline: "<rowIndex>.<cell>" →
  // the PUBLISHED value. Parity with a scalar field's DeployedText — a cell that
  // differs from its published value shows the revert button.
  publishedCells?: Record<string, string>;
  // baseURL/locale/slug + repeaterContext/repeaterSection (Task 8B) let this
  // controller POST the operator-UI row-delete endpoint itself for a
  // PERSISTED row's removal, mirroring image-picker.ts's self-contained
  // removeFromPool. repeaterContext/repeaterSection are the EXACT
  // (editContext, section) pair CMSEditorField.RepeaterContext/RepeaterSection
  // carries — see that field's Go doc comment for why the client can't safely
  // reconstruct this from `target` alone. repeaterContext is EMPTY for a
  // repeater with no safe chokepoint route (currently: site:social_links,
  // DB-derived, not a content/* array) — removeRow then falls back to the
  // pre-existing splice+emit path for every row, persisted or not.
  baseURL: string;
  locale: string;
  slug?: string;
  repeaterContext?: string;
  repeaterSection?: string;
  // removeFailedMsg is the i18n-translated toast shown when the remove-row
  // POST fails (CLAUDE.md: no hardcoded strings in client code).
  removeFailedMsg?: string;
  // maxRows caps the row count (0/absent = unlimited).
  maxRows?: number;
}

export function cmsRepeater(config: CMSRepeaterConfig) {
  let _seq = 0;
  // Seed rows are server-loaded on mount — persisted from the controller's
  // point of view (see RepeaterRow._persisted's doc comment).
  const seed: RepeaterRow[] = (config.rows || []).map(
    (r) => ({ ...r, _k: _seq++, _persisted: true }) as RepeaterRow,
  );

  return {
    field: config.field,
    target: config.target,
    schema: config.schema || [],
    rows: seed as RepeaterRow[],
    // aiCells: "<rowIndex>.<cell>" → pre-assistant value. Presence drives the
    // inline badge + revert (hasAI); the value is the 1-level revert target.
    aiCells: { ...(config.aiCells || {}) } as Record<string, string>,
    aiLeafBase: config.aiLeafBase || "",
    // publishedCells: "<rowIndex>.<cell>" → published value (manual-revert baseline).
    publishedCells: { ...(config.publishedCells || {}) } as Record<string, string>,
    baseURL: config.baseURL,
    locale: config.locale,
    slug: config.slug || "",
    repeaterContext: config.repeaterContext || "",
    repeaterSection: config.repeaterSection || "",
    removeFailedMsg: config.removeFailedMsg || "",
    maxRows: config.maxRows || 0,
    _sortable: null as Sortable | null,

    init() {
      // SortableJS lives on the stable <ul>; Alpine's keyed x-for only swaps
      // the <li> children, so the instance survives row add/remove without
      // re-binding. Tear down when the component is removed (section swap).
      (this as any).$nextTick(() => this._initSortable());
    },

    // A fresh row with every schema cell initialized to "" and a new client key.
    // Never persisted — see RepeaterRow._persisted's doc comment.
    _blankRow(): RepeaterRow {
      const row = { _k: _seq++, _persisted: false } as RepeaterRow;
      for (const cell of this.schema) row[cell.key] = "";
      return row;
    },

    addRow() {
      this.rows = [...this.rows, this._blankRow()];
      // New row is empty → no value change worth persisting yet; emit only on
      // edit/remove/reorder. (Persisting blank rows would write empty {icon,text}.)
    },

    // canAddRow reports whether another row can still hold a valid value.
    // FIX B: a `unique` select cell (e.g. seasonal's 4 seasons, anreise's 3
    // modes) can only ever take as many non-blank rows as it has options —
    // every option is already :disabled once used elsewhere (see the templ),
    // so a further row can never pick a valid value for that cell. Gates the
    // Add button (x-show). When no schema cell is `unique`, always true
    // (unlimited rows).
    canAddRow(): boolean {
      if (this.maxRows > 0 && this.rows.length >= this.maxRows) return false;
      return !this.schema.some(
        (c: RepeaterCell) => c.unique && Array.isArray(c.options) && this.rows.length >= c.options.length,
      );
    },

    // removeRow deletes row `idx`. A PERSISTED, NON-BLANK row (server-loaded,
    // not this session's unsaved add — see RepeaterRow._persisted — and
    // currently holding at least one non-empty cell, see _isBlankRow) with a
    // known repeaterContext routes through _removePersistedRow (the
    // operator-UI row-delete chokepoint, Task 8B). Everything else — an
    // unsaved row, a blank row (never persisted, or un-persisted by a prior
    // autosave's emit() filter — see _isBlankRow), or a repeater with no safe
    // chokepoint route (repeaterContext empty, e.g. site:social_links) —
    // stays the pure client splice + array-blind emit() (there is either no
    // server-side row to reindex, or no safe endpoint to reindex it through).
    removeRow(idx: number) {
      const row = this.rows[idx];
      if (!row) return;
      if (!row._persisted || !this.repeaterContext || this._isBlankRow(row)) {
        this.rows = this.rows.filter((_, i) => i !== idx);
        this.emit();
        return;
      }
      void this._removePersistedRow(idx);
    },

    // _isBlankRow mirrors emit()'s per-row "blank" predicate EXACTLY (same
    // schema-projected cell values via String(...), same "every cell === ''"
    // test — emit() drops a row when Object.values(o).some(v => v !== '')
    // is false) so blank-row detection can never diverge between the two call
    // sites. A blank row is never in the SERVER array (emit() strips it on
    // every autosave), so it must never be routed through the row-delete
    // endpoint — whether it's an unsaved addRow() row or a once-persisted
    // row the operator has since cleared every cell of (_persisted is set
    // once at load and never revisited, so it can go stale).
    _isBlankRow(row: RepeaterRow): boolean {
      return this.schema.every((cell) => String(row[cell.key] ?? "") === "");
    },

    // _serverIndexFor computes the position row `idx` occupies in the SERVER
    // array — the exact array emit() would produce (schema-projected cell
    // values, blank rows dropped, in original order) — by counting non-blank
    // rows before `idx` in the CLIENT array. This is the index the
    // remove-row endpoint must receive: the client array can hold blank
    // "phantom" rows (an unsaved addRow() the operator dragged earlier via
    // SortableJS, before typing anything into it) that emit() never
    // persisted, so the raw client index and the true server index diverge
    // whenever a phantom sits before the row being deleted.
    //
    // Example: server holds [A, B, C] (3 persisted, non-blank rows). Operator
    // clicks "add row" (blank, unpersisted) then drags it to the front ->
    // client = [blank, A, B, C]. Deleting B is now client idx 2, but only A
    // precedes B among NON-BLANK client rows, so this returns 1 — the
    // correct server index — instead of the buggy raw 2 (which would have
    // spliced C, the wrong row, out of the server array).
    _serverIndexFor(idx: number): number {
      let n = 0;
      for (let i = 0; i < idx; i++) {
        if (!this._isBlankRow(this.rows[i])) n++;
      }
      return n;
    },

    // _removePersistedRow POSTs the operator-UI row-delete endpoint so the
    // removal runs through the SAME transactional splice + ReindexOnRemoveOn
    // chokepoint the assistant's remove tool (RemoveListItem) uses — an
    // array-blind whole-array emit() here would silently drift every
    // surviving row's scalar cell provenance (items.N.cell) by one, the exact
    // bug this task closes. The POSTed `index` is the SERVER index (see
    // _serverIndexFor), NOT the raw client `idx` — the two diverge whenever a
    // blank "phantom" row sits before the deleted row in the client array
    // (see _serverIndexFor's doc comment for the concrete example). Optimistic
    // splice for a snappy UI; on success a bubbling cms-row-removed event asks
    // the editor to refresh the leftpane from the server (editor.ts's
    // listener) so aiCells/publishedCells/indices re-seed authoritative — this
    // controller does not try to reconcile those maps itself. On failure the
    // row is restored and a toast fires.
    //
    // Residual gap (Task 8B follow-up, NOT closed here): the computed server
    // index is only as fresh as the CLIENT array. If a preceding reorder's
    // emit() is still sitting in editor.ts's debounced autosave queue (up to
    // 600ms — see editor.ts _queueEdit/_saveTimer) when this delete fires, the
    // server hasn't applied that reorder yet and the computed index can still
    // point at the wrong row. Closing this cleanly would need a synchronous
    // handle to editor.ts's flushPending(), but the live instance is
    // editor.ts's module-private `_activeEditor`, reachable from sibling
    // controllers (this file included, via emit()) only through fire-and-
    // forget bubbling DOM events, not a request/response call — so no new
    // cross-module primitive is invented here; this is left as a known gap.
    async _removePersistedRow(idx: number) {
      const removed = this.rows[idx];
      // Compute BEFORE splicing — _serverIndexFor needs the pre-splice array.
      const serverIndex = this._serverIndexFor(idx);
      this.rows = this.rows.filter((_, i) => i !== idx);
      try {
        const resp = await fetch(`${this.baseURL}/api/content/remove-row`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({
            context: this.repeaterContext,
            locale: this.locale,
            slug: this.slug,
            section: this.repeaterSection,
            array_field: this.field,
            index: serverIndex,
          }),
        });
        if (!resp.ok) throw new Error(`remove row ${resp.status}`);
        // Raw fetch: re-arm the publish surfaces explicitly (plan 2 D11).
        signalPublishStateChanged();
        ((this as any).$el as HTMLElement).dispatchEvent(
          new CustomEvent("cms-row-removed", { bubbles: true }),
        );
      } catch (err) {
        console.error("cms repeater row remove failed", err);
        // Best-effort restore at the original position so the operator doesn't
        // lose the row on a transient failure.
        this.rows = [...this.rows.slice(0, idx), removed, ...this.rows.slice(idx)];
        if (this.removeFailedMsg) dispatchToast(this.removeFailedMsg, "error");
      }
    },

    _initSortable() {
      const el = (this as any).$refs.rowList as HTMLElement | undefined;
      if (!el) return;
      this._teardownSortable();
      this._sortable = createReorderable(el, {
        handle: "[data-sort-handle]",
        draggable: "[data-row]",
        onEnd: (e: Sortable.SortableEvent) => this._onReorderEnd(el, e),
      });
    },

    // _onReorderEnd applies a drag-reorder and re-keys the index-keyed revert
    // baselines (publishedCells/aiCells) to follow the moved rows. Extracted
    // from the onEnd closure so it's directly callable from tests (SortableJS
    // itself isn't exercised there — see repeater.test.ts's sortablejs mock).
    _onReorderEnd(el: HTMLElement, e: Sortable.SortableEvent) {
      // Capture each row's PRE-reorder index by its stable `_k` before the
      // splice below moves it.
      const oldIndexByK = new Map<number, number>(this.rows.map((r, i) => [r._k, i]));
      this.rows = applySortableReorder(el, e, this.rows, "data-row");
      // A pure reorder changes no cell VALUE, only order. publishedCells/aiCells
      // are "<rowIndex>.<cell>"-keyed, so re-key them to follow the rows —
      // otherwise the value at each index diverges from its index-keyed
      // baseline (spurious revert buttons) and revertCell restores the value
      // the PREVIOUS occupant of that index held (e.g. winter's title -> autumn).
      this.publishedCells = this._remapCellBaseline(this.publishedCells, oldIndexByK);
      this.aiCells = this._remapCellBaseline(this.aiCells, oldIndexByK);
      this.emit();
    },

    // _remapCellBaseline re-keys an index-keyed baseline map (publishedCells
    // or aiCells) from OLD row indices to NEW row indices after a reorder,
    // following each row by its stable `_k` rather than its position. A row
    // with no baseline entry for a given cell (not in `oldMap`) stays absent
    // at its new index — only present keys are carried forward.
    _remapCellBaseline(
      oldMap: Record<string, string>,
      oldIndexByK: Map<number, number>,
    ): Record<string, string> {
      const next: Record<string, string> = {};
      this.rows.forEach((row: RepeaterRow, newIdx: number) => {
        const oldIdx = oldIndexByK.get(row._k);
        if (oldIdx === undefined) return;
        for (const cell of this.schema) {
          const oldKey = oldIdx + "." + cell.key;
          if (Object.prototype.hasOwnProperty.call(oldMap, oldKey)) {
            next[newIdx + "." + cell.key] = oldMap[oldKey];
          }
        }
      });
      return next;
    },

    _teardownSortable() {
      if (this._sortable) {
        this._sortable.destroy();
        this._sortable = null;
      }
    },

    destroy() {
      this._teardownSortable();
    },

    // hasAI reports whether row `idx`'s `cellKey` cell carries a pending,
    // unreviewed assistant edit — drives the inline badge + revert affordance.
    // It reads this.aiCells[k] (a tracked `get`) rather than hasOwnProperty:
    // Alpine's bundled @vue/reactivity proxy has no getOwnPropertyDescriptor
    // trap, so hasOwnProperty registers NO per-key dependency and the
    // x-if/:class effects wouldn't re-run when a key is added (live edit) or
    // deleted (revert/override) — the badge would only update on reload. Stored
    // values are always strings (incl. ""), so `!== undefined` is an exact
    // "key present" test, and `get` + add/delete are both tracked.
    hasAI(idx: number, cellKey: string): boolean {
      return this.aiCells[idx + "." + cellKey] !== undefined;
    },

    // hasRevert reports whether row `idx`'s `cellKey` cell has anything below
    // its current value to pop back to — a pending assistant edit (pre-AI) OR a
    // manual edit away from the published value. Drives the revert button's
    // visibility so list cells match scalar fields (which show revert on any
    // manual edit, not just assistant edits). Reads row[cellKey] reactively so
    // the button appears/disappears live as the operator types.
    hasRevert(idx: number, cellKey: string): boolean {
      if (this.hasAI(idx, cellKey)) return true;
      const pub = this.publishedCells[idx + "." + cellKey];
      if (pub === undefined) return false;
      const cur = this.rows[idx] ? String(this.rows[idx][cellKey] ?? "") : "";
      return cur !== pub;
    },

    // _aiLeafPath builds the AI-edit-store leaf path for one cell (aiLeafBase +
    // ".<rowIndex>.<cell>") — the exact key the write tool recorded, so the ack
    // clears the right row. Empty when the field wasn't seeded with a base.
    _aiLeafPath(idx: number, cellKey: string): string {
      return this.aiLeafBase ? `${this.aiLeafBase}.${idx}.${cellKey}` : "";
    },

    // _ackCell drops a cell's local AI mark (badge disappears) and asks the
    // editor to clear the server-side AI-edit row (POST .../edits/ack). The
    // editor owns the fetch (it holds the content path + CSRF); we bubble a
    // cms-ai-cell-ack event carrying the leaf. No-op if the cell has no mark.
    _ackCell(idx: number, cellKey: string) {
      const k = idx + "." + cellKey;
      if (this.aiCells[k] === undefined) return;
      const leaf = this._aiLeafPath(idx, cellKey);
      delete this.aiCells[k];
      if (leaf) {
        (this as unknown as { $el: HTMLElement }).$el.dispatchEvent(
          new CustomEvent("cms-ai-cell-ack", { detail: { leafPath: leaf }, bubbles: true }),
        );
      }
    },

    // _onCellEdit runs when the operator manually edits a text cell. If that
    // cell was assistant-edited, this is an override (design's chosen behavior):
    // resolve the AI layer — drop the badge + clear the server row — while
    // keeping the operator's own value (the edit itself persists via emit).
    // A no-op once the mark is gone, so calling it on every keystroke is cheap.
    _onCellEdit(idx: number, cellKey: string) {
      this._ackCell(idx, cellKey);
    },

    // revertCell pops the cell's revert stack one level (parity with the scalar
    // 2-level revert, editor.ts _onRevertFieldClick): the pending assistant
    // edit's pre-AI value first (if present and the cell still holds the
    // assistant's value), then the published baseline. Client-driven: set the
    // cell, persist the whole array (emit — the array autosave is opaque to
    // per-cell AI-clearing since the clobber fix, so siblings are safe), and,
    // when it popped the AI layer, clear that cell's AI-edit row via _ackCell.
    revertCell(idx: number, cellKey: string) {
      const k = idx + "." + cellKey;
      const cur = this.rows[idx] ? String(this.rows[idx][cellKey] ?? "") : "";
      const preAI = this.aiCells[k];
      const pub = this.publishedCells[k];
      let next: string | null = null;
      let poppedAI = false;
      if (preAI !== undefined && cur !== preAI) {
        next = preAI; // pop the assistant layer → pre-AI value
        poppedAI = true;
      } else if (pub !== undefined && cur !== pub) {
        next = pub; // pop → published baseline
      }
      if (next === null) return;
      if (this.rows[idx]) this.rows[idx][cellKey] = next;
      // Clear the AI mark + server row only when we actually resolved the AI
      // layer; a manual-edit revert-to-published has no AI row to ack.
      if (poppedAI) this._ackCell(idx, cellKey);
      // Persist with blank rows KEPT: reverting a cell to an empty value can
      // blank a whole row, and the default emit()'s blank-row filter would then
      // drop it — silently losing the row AND (since provenance is row-index-
      // keyed) shifting every later row's badge/ack leaf by one until reload.
      // Keeping the row makes revert a faithful restore of the array shape.
      this.emit(true);
    },

    // emit dispatches the bubbling cms-image-changed event the cmsEditor
    // controller autosaves as a structured value (its detail.value path, NOT
    // the string `input` path). The payload is the rows array with `_k`/
    // `_persisted` stripped — exactly the {icon,text,…} shape the content
    // JSON stores. FIX A (data loss): a row may carry a STRING field that
    // isn't in the schema — e.g. the seasonal repeater's image_id, written by
    // a SEPARATE image surface into seasonal.blocks[i].image_id — and this
    // repeater's own write must never clobber it. So `out` starts from the
    // row's own string fields (preserving image_id etc.), THEN the schema
    // cells overwrite on top (the authoritative source for cells the operator
    // can actually edit here). Blank rows are dropped by default so a
    // half-added row doesn't persist noise; pass keepBlankRows=true
    // (revertCell) to preserve array shape when a legitimate restore may
    // blank a row. Blank-ness is judged on SCHEMA cells only — a row holding
    // only a non-schema field (e.g. an orphaned image_id with no season/body)
    // is still blank and gets dropped, same as parseRepeaterRows' gate.
    emit(keepBlankRows = false) {
      const mapped = this.rows.map((r) => {
        const out: Record<string, string> = {};
        for (const k in r) {
          if (k === "_k" || k === "_persisted") continue;
          const v = (r as Record<string, unknown>)[k];
          if (typeof v === "string") out[k] = v; // preserve image_id etc.
        }
        for (const cell of this.schema) out[cell.key] = String(r[cell.key] ?? "");
        return out;
      });
      const value = keepBlankRows
        ? mapped
        : mapped.filter((o) => this.schema.some((c: RepeaterCell) => (o[c.key] ?? "") !== ""));
      ((this as any).$el as HTMLElement).dispatchEvent(
        new CustomEvent("cms-image-changed", {
          detail: { field: this.field, target: this.target, value },
          bubbles: true,
        }),
      );
    },
  };
}
